import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import DOMPurify from 'dompurify';
import { apiGet, apiPost } from './api';

const sanitizarHtml = (html) => {
    if (!html) return '';
    return DOMPurify.sanitize(html.replace(/&nbsp;/g, ' '));
};

const aplicarMascaraTelefone = (valor) => {
    const numeros = valor.replace(/\D/g, '').slice(0, 11);
    if (numeros.length <= 2) return numeros;
    if (numeros.length <= 6) return `(${numeros.slice(0, 2)}) ${numeros.slice(2)}`;
    if (numeros.length <= 10) return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 6)}-${numeros.slice(6)}`;
    return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 3)} ${numeros.slice(3, 7)}-${numeros.slice(7)}`;
};

export default function Player() {
    const { id } = useParams();
    const [formConfig, setFormConfig] = useState(null);
    const [formId, setFormId] = useState(null); // id interno real (a URL pode estar usando a slug)
    const [erro, setErro] = useState('');

    const [sessionId] = useState(() => crypto.randomUUID());
    const [telaAtual, setTelaAtual] = useState('loading');
    const [indiceAtual, setIndiceAtual] = useState(0);
    const [respostas, setRespostas] = useState({});
    const [respostaAtual, setRespostaAtual] = useState('');
    const [telaFinalId, setTelaFinalId] = useState(null);
    const [formIniciado, setFormIniciado] = useState(false);
    const [erroInput, setErroInput] = useState('');
    const [enviando, setEnviando] = useState(false);

    // === LÓGICA DA SETA DE SCROLL CORRIGIDA ===
    const scrollContainerRef = useRef(null);
    const [mostrarSetaScroll, setMostrarSetaScroll] = useState(false);

    const verificarScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
            const scrollBottom = Math.ceil(scrollTop + clientHeight);
            
            // Só mostra a seta se realmente tiver mais de 20px escondidos para baixo
            setMostrarSetaScroll(scrollHeight - scrollBottom > 20);
        }
    };

    useEffect(() => {
        // Dispara em 3 momentos para garantir que a animação terminou e as imagens carregaram
        const t1 = setTimeout(verificarScroll, 100);
        const t2 = setTimeout(verificarScroll, 500);
        const t3 = setTimeout(verificarScroll, 1000);
        
        window.addEventListener('resize', verificarScroll);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            window.removeEventListener('resize', verificarScroll);
        };
    }, [telaAtual, indiceAtual]);
    // ===========================================

    // Dispara o evento nos dois lados: Pixel no navegador (fbq) e Conversions API no servidor
    // (evento_capi.php — o token da CAPI nunca chega no navegador). Mesmo event_id nos dois
    // lados (sessionId) para a Meta deduplicar.
    const registrarEventoCapi = (eventName, idParaBusca) => {
        if (!idParaBusca) return;
        apiPost('/evento_capi.php', {
            form_id: idParaBusca,
            session_id: sessionId,
            event_name: eventName,
            origem_url: window.location.href
        }).catch(() => {});
    };

    const dispararFormStarted = () => {
        if (!formIniciado && window.fbq) {
            window.fbq('trackCustom', 'FormStarted', { form_id: id }, { eventID: sessionId });
            registrarEventoCapi('FormStarted', formId);
            setFormIniciado(true);
        }
    };

    useEffect(() => {
        document.title = "Unity Makers - Formulário";
        const buscarForm = async () => {
            try {
                const dados = await apiGet(`/get_formulario.php?id=${id}`);
                if (dados.sucesso) {
                    setFormConfig(dados.form);
                    setFormId(dados.id || id);
                    if (dados.form.tela_inicial && dados.form.tela_inicial.mostrar) {
                        setTelaAtual('inicio');
                    } else {
                        setTelaAtual('perguntas');
                    }

                    if (dados.pixel_id) {
                        if (!window.fbq) {
                            /* eslint-disable */
                            !function (f, b, e, v, n, t, s) {
                                if (f.fbq) return; n = f.fbq = function () {
                                    n.callMethod ?
                                        n.callMethod.apply(n, arguments) : n.queue.push(arguments)
                                };
                                if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
                                n.queue = []; t = b.createElement(e); t.async = !0;
                                t.src = v; s = b.getElementsByTagName(e)[0];
                                s.parentNode.insertBefore(t, s)
                            }(window, document, 'script',
                                'https://connect.facebook.net/en_US/fbevents.js');
                            /* eslint-enable */
                        }

                        window.fbq('init', dados.pixel_id);
                        window.fbq('track', 'PageView');
                        window.fbq('trackCustom', 'FormOpened', { form_id: id }, { eventID: sessionId });
                        registrarEventoCapi('FormOpened', dados.id || id);
                    }
                } else setErro("Formulário não encontrado.");
            } catch (e) { setErro("Erro de conexão."); }
        };
        buscarForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, sessionId]);

    if (erro) return <div style={{ padding: '50px', textAlign: 'center', fontFamily: '"Inter", sans-serif' }}>{erro}</div>;
    if (!formConfig || telaAtual === 'loading') return <div style={{ padding: '50px', textAlign: 'center', fontFamily: '"Inter", sans-serif' }}>Carregando formulário...</div>;

    const perguntaAtual = formConfig.perguntas[indiceAtual];
    const telaFinalAtiva = formConfig.telas_finais?.find(t => t.id === telaFinalId) || formConfig.telas_finais?.[0];
    const ehStatement = perguntaAtual?.tipo === 'statement';

    let progresso = 0;
    if (telaAtual === 'final') progresso = 100;
    else if (telaAtual === 'perguntas') progresso = ((indiceAtual) / formConfig.perguntas.length) * 100;

    const salvarLeadNaHostinger = async (dadosRespostas, statusFinal) => {
        try {
            const resposta = await apiPost('/salvar_lead.php', {
                session_id: sessionId,
                form_id: formId || id,
                respostas: dadosRespostas,
                status: statusFinal,
                origem_url: window.location.href
            });
            return resposta;
        } catch (e) {
            console.log("Erro ao salvar no background");
            return null;
        }
    };

    const iniciarFormulario = () => {
        setTelaAtual('perguntas');
        dispararFormStarted();
    };

    const avancar = async (respostaForcada = null) => {
        if (!perguntaAtual || enviando) return;
        const respostaFinal = respostaForcada !== null ? respostaForcada : respostaAtual;
        if (!ehStatement && (!respostaFinal || respostaFinal.trim() === '')) return;

        setErroInput('');

        if (perguntaAtual.tipo === 'email') {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(respostaFinal)) {
                setErroInput("Por favor, insira um e-mail válido com @ e provedor.");
                return;
            }
        }

        if (perguntaAtual.tipo === 'phone') {
            const apenasNumeros = respostaFinal.replace(/\D/g, '');
            const tamanhoValido = apenasNumeros.length === 10 || apenasNumeros.length === 11;

            if (!tamanhoValido) { setErroInput("Por favor, insira um telefone válido com 10 ou 11 dígitos (com DDD)."); return; }
            if (/^(.)\1+$/.test(apenasNumeros)) { setErroInput("Por favor, insira um telefone válido."); return; }
            if (/(.)\1{4,}/.test(apenasNumeros)) { setErroInput("Por favor, insira um telefone válido."); return; }
            if (apenasNumeros.length === 11 && apenasNumeros[2] !== '9') { setErroInput("Celulares de 11 dígitos devem começar com '9' após o DDD."); return; }
        }

        if (perguntaAtual.tipo === 'short_text') {
            if (/(.)\1{3,}/.test(respostaFinal) || /[bcdfghjklmnpqrstvwxyz]{5,}/i.test(respostaFinal)) {
                setErroInput("Por favor, insira um texto válido.");
                return;
            }
        }

        dispararFormStarted();

        const tituloLimpo = perguntaAtual.titulo.replace(/<[^>]*>?/gm, '').replace(/&nbsp;/g, ' ');
        const novasRespostas = { ...respostas, [tituloLimpo]: respostaFinal || 'OK (Declaração)' };
        setRespostas(novasRespostas);

        let proximoIndice = indiceAtual + 1;
        let irParaFinalId = null;

        if (perguntaAtual.logicas && perguntaAtual.logicas.length > 0) {
            for (let regra of perguntaAtual.logicas) {
                let atende = false;
                if (regra.operador === '==' && respostaFinal.toLowerCase() === regra.valor.toLowerCase()) atende = true;
                if (regra.operador === '!=' && respostaFinal.toLowerCase() !== regra.valor.toLowerCase()) atende = true;
                if (atende) {
                    if (regra.destino.startsWith('tf') || regra.destino === 'final') {
                        irParaFinalId = regra.destino === 'final' ? formConfig.telas_finais[0].id : regra.destino;
                    } else {
                        const idxDestino = formConfig.perguntas.findIndex(p => p.id === regra.destino);
                        if (idxDestino !== -1) proximoIndice = idxDestino;
                    }
                    break;
                }
            }
        }

        if (proximoIndice >= formConfig.perguntas.length && !irParaFinalId) {
            irParaFinalId = formConfig.telas_finais?.[0]?.id || 'final';
        }

        setEnviando(true);
        try {
            const respostaSalvar = await salvarLeadNaHostinger(novasRespostas, irParaFinalId ? 'completa' : 'parcial');

            if (irParaFinalId) {
                setTelaFinalId(irParaFinalId);
                setTelaAtual('final');
                if (window.fbq) {
                    window.fbq('trackCustom', 'FormSubmitted', { form_id: id }, { eventID: sessionId });
                    if (respostaSalvar?.qualificado) {
                        window.fbq('trackCustom', 'LeadQualificado', { form_id: id }, { eventID: sessionId + '_qualificado' });
                    }
                }
            } else {
                setIndiceAtual(proximoIndice);
                setRespostaAtual('');
            }
        } finally {
            setEnviando(false);
        }
    };

    const pressionarEnter = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            avancar();
        }
    };

    const selecionarOpcaoMultipla = (opcao) => {
        if (enviando) return;
        setRespostaAtual(opcao);
        setErroInput('');
        setTimeout(() => avancar(opcao), 100);
    };

    const converterUrlYouTube = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : url;
    };

    const estiloContainer = {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: '6px',
        overflowY: 'auto', overflowX: 'hidden', backgroundColor: '#fff',
        fontFamily: '"Inter", sans-serif', color: '#111827',
    };

    return (
        <div style={{ fontFamily: '"Inter", sans-serif' }}>
            <style>{`
                html, body { margin: 0; padding: 0; overflow: hidden; height: 100%; }
                .html-formatado, .html-formatado * { word-break: normal !important; overflow-wrap: break-word !important; white-space: normal !important; hyphens: none !important; }
                .html-formatado p { margin: 0 0 10px 0; padding: 0; line-height: 1.5; }
                .html-formatado p:last-child { margin-bottom: 0; }
                .html-formatado img { max-width: 100%; height: auto; }
                .html-formatado strong, .html-formatado b { font-weight: 800 !important; color: #111827 !important; }

                .html-formatado .ql-align-center { text-align: center !important; }
                .html-formatado .ql-align-left { text-align: left !important; }
                .html-formatado .ql-align-right { text-align: right !important; }
                .html-formatado .ql-align-justify { text-align: justify !important; }

                .titulo-principal { font-size: 32px; margin-bottom: 15px; font-weight: 400; line-height: 1.3; color: #111827; }
                .descricao-principal { font-size: 18px; color: #4b5563; margin-bottom: 30px; text-align: left; font-weight: 400; }
                .pergunta-titulo { font-size: 24px; font-weight: 400; margin-bottom: 25px; display: flex; align-items: flex-start; width: 100%; color: #111827; }

                .input-resposta { width: 100%; font-size: 22px; padding: 10px 0; border: none; border-bottom: 2px solid #111827; background: transparent; outline: none; margin-bottom: 15px; color: #3b82f6; font-family: "Inter", sans-serif; box-sizing: border-box; }
                .btn-avancar { background: #111827; color: white; border: none; padding: 14px 32px; border-radius: 8px; font-size: 18px; font-weight: 700; cursor: pointer; box-shadow: 0 4px 6px rgba(0,0,0,0.1); font-family: "Inter", sans-serif; transition: all 0.2s; display: inline-block; }
                .btn-avancar:hover { transform: translateY(-2px); box-shadow: 0 6px 12px rgba(0,0,0,0.15); }
                .logo-img { max-width: 100%; max-height: 130px; margin-bottom: 25px; border-radius: 8px; }

                .tela-wrapper { min-height: 100%; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; box-sizing: border-box; }
                .tela-wrapper-centralizado { min-height: 100%; display: flex; align-items: center; justify-content: center; padding: 40px 20px; box-sizing: border-box; }

                /* ANIMAÇÃO DA SETA DE ROLAGEM CORRIGIDA PARA CENTRALIZAR PERFEITAMENTE */
                .seta-scroll { animation: bounceScroll 2s infinite; }
                @keyframes bounceScroll {
                    0%, 20%, 50%, 80%, 100% { transform: translateX(-50%) translateY(0); }
                    40% { transform: translateX(-50%) translateY(-10px); }
                    60% { transform: translateX(-50%) translateY(-5px); }
                }

                @media (max-width: 600px) {
                    .titulo-principal { font-size: 24px; margin-bottom: 12px; }
                    .descricao-principal { font-size: 16px; margin-bottom: 25px; }
                    .pergunta-titulo { font-size: 20px; margin-bottom: 20px; }
                    .input-resposta { font-size: 18px; }
                    .btn-avancar { width: 100%; padding: 15px 20px; font-size: 18px; }
                    .logo-img { max-height: 80px; margin-bottom: 20px; }
                    .tela-wrapper { padding: 30px 15px; }
                    .tela-wrapper-centralizado { padding: 30px 15px; align-items: flex-start; }
                }
            `}</style>

            <div style={estiloContainer} ref={scrollContainerRef} onScroll={verificarScroll}>
                <AnimatePresence mode="wait">

                    {telaAtual === 'inicio' && formConfig.tela_inicial && (
                        <motion.div key="inicio" className="tela-wrapper-centralizado" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -40 }} transition={{ duration: 0.4 }}>
                            <div style={{ width: '100%', maxWidth: '600px', textAlign: 'center', boxSizing: 'border-box' }}>
                                {formConfig.tela_inicial.imagem_url && <img src={formConfig.tela_inicial.imagem_url} alt="Logo" className="logo-img" />}
                                <div className="titulo-principal html-formatado" dangerouslySetInnerHTML={{ __html: sanitizarHtml(formConfig.tela_inicial.titulo) }} />
                                <div className="descricao-principal html-formatado" dangerouslySetInnerHTML={{ __html: sanitizarHtml(formConfig.tela_inicial.descricao) }} />
                                <button onClick={iniciarFormulario} className="btn-avancar">{formConfig.tela_inicial.botao || 'Começar'}</button>
                            </div>
                        </motion.div>
                    )}

                    {telaAtual === 'perguntas' && perguntaAtual && ehStatement && (
                        <motion.div key={perguntaAtual.id} className="tela-wrapper" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -40 }} transition={{ duration: 0.4, ease: "easeOut" }}>
                            <div style={{ width: '100%', maxWidth: '600px', boxSizing: 'border-box' }}>
                                <div className="pergunta-titulo">
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div className="html-formatado" dangerouslySetInnerHTML={{ __html: sanitizarHtml(perguntaAtual.titulo) }} />
                                        {perguntaAtual.descricao && <div className="descricao-principal html-formatado" style={{ marginTop: '15px', marginBottom: 0 }} dangerouslySetInnerHTML={{ __html: sanitizarHtml(perguntaAtual.descricao) }} />}
                                    </div>
                                </div>
                                <div style={{ marginTop: '30px' }}><button onClick={() => avancar(null)} disabled={enviando} className="btn-avancar" style={{ width: '100%', padding: '18px', fontSize: '18px', opacity: enviando ? 0.6 : 1 }}>{perguntaAtual.botao || 'OK'}</button></div>
                            </div>
                        </motion.div>
                    )}

                    {telaAtual === 'perguntas' && perguntaAtual && !ehStatement && (
                        <motion.div key={perguntaAtual.id} className="tela-wrapper-centralizado" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -40 }} transition={{ duration: 0.4, ease: "easeOut" }}>
                            <div style={{ width: '100%', maxWidth: '600px', boxSizing: 'border-box' }}>
                                <div className="pergunta-titulo">
                                    <span style={{ color: '#3b82f6', marginRight: '10px', marginTop: '2px', flexShrink: 0, fontWeight: 'bold' }}>{indiceAtual + 1} →</span>
                                    <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                                        <div className="html-formatado" dangerouslySetInnerHTML={{ __html: sanitizarHtml(perguntaAtual.titulo) }} />
                                    </div>
                                </div>

                                {perguntaAtual.tipo === 'multiple_choice' ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {(perguntaAtual.opcoes || []).map((op, i) => (
                                            <div key={i} onClick={() => selecionarOpcaoMultipla(op)} style={{ padding: '14px 20px', border: respostaAtual === op ? '2px solid #3b82f6' : '1px solid #d1d5db', backgroundColor: respostaAtual === op ? '#eff6ff' : '#fff', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '15px', transition: 'all 0.2s', fontWeight: '500' }}>
                                                <span style={{ background: respostaAtual === op ? '#3b82f6' : '#f3f4f6', color: respostaAtual === op ? '#fff' : '#4b5563', borderRadius: '4px', padding: '2px 8px', fontSize: '12px', fontWeight: 'bold', flexShrink: 0 }}>{String.fromCharCode(65 + i)}</span>{op}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type={perguntaAtual.tipo === 'email' ? 'email' : perguntaAtual.tipo === 'phone' ? 'tel' : 'text'}
                                            value={respostaAtual}
                                            onChange={(e) => { const valor = perguntaAtual.tipo === 'phone' ? aplicarMascaraTelefone(e.target.value) : e.target.value; setRespostaAtual(valor); setErroInput(''); dispararFormStarted(); }}
                                            onKeyDown={pressionarEnter}
                                            placeholder={perguntaAtual.tipo === 'email' ? "ex: contato@email.com" : perguntaAtual.tipo === 'phone' ? "ex: (53) 9 8151-5893" : "Digite sua resposta..."}
                                            className="input-resposta"
                                        />
                                        {erroInput && <p style={{ color: '#ef4444', fontSize: '14px', margin: '0 0 15px 0', fontWeight: 'bold' }}>{erroInput}</p>}
                                        <div style={{ marginTop: '20px' }}>
                                            <button onClick={() => avancar(null)} disabled={enviando} className="btn-avancar" style={{ opacity: enviando ? 0.6 : 1 }}>{perguntaAtual.botao || 'OK'}</button>
                                            <span style={{ fontSize: '14px', color: '#6b7280', marginLeft: '15px' }}>pressione <b>Enter ↵</b></span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    )}

                    {telaAtual === 'final' && telaFinalAtiva && (
                        <motion.div key="final" className="tela-wrapper" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} style={{ width: '100%' }}>
                            <div style={{ width: '100%', maxWidth: '600px', textAlign: 'center', boxSizing: 'border-box' }}>
                                <div className="titulo-principal html-formatado" style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: sanitizarHtml(telaFinalAtiva.titulo) }} />
                                <div className="descricao-principal html-formatado" style={{ textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: sanitizarHtml(telaFinalAtiva.descricao) }} />

                                {(telaFinalAtiva.midia_tipo === 'video' || (!telaFinalAtiva.midia_tipo && telaFinalAtiva.video_url)) && telaFinalAtiva.video_url && (
                                    <div style={{ marginBottom: '30px', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                                        <iframe title="Vídeo da tela final" width="100%" height="315" src={converterUrlYouTube(telaFinalAtiva.video_url)} frameBorder="0" allowFullScreen></iframe>
                                    </div>
                                )}
                                {telaFinalAtiva.midia_tipo === 'image' && telaFinalAtiva.imagem_url && (
                                    <div style={{ marginBottom: '30px', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                                        <img src={telaFinalAtiva.imagem_url} alt="Mídia Final" style={{ width: '100%', display: 'block' }} />
                                    </div>
                                )}

                                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    {(telaFinalAtiva.botoes || []).map(b => (
                                        <button key={b.id} onClick={() => window.location.href = b.url || '#'} style={{ flex: 1, minWidth: '200px', padding: '14px 24px', background: b.cor_fundo || '#111827', color: b.cor_texto || '#ffffff', border: `2px solid ${b.cor_borda || '#111827'}`, borderRadius: '8px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', fontFamily: '"Inter", sans-serif' }}>
                                            {b.texto}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </motion.div>
                    )}

                </AnimatePresence>
            </div>

            {mostrarSetaScroll && (
                <div
                    onClick={() => { scrollContainerRef.current?.scrollBy({ top: 300, behavior: 'smooth' }); }}
                    className="seta-scroll"
                    style={{
                        position: 'fixed', bottom: '25px', left: '50%', width: '40px', height: '40px', 
                        backgroundColor: '#3b82f6', color: 'white', borderRadius: '50%', display: 'flex', 
                        alignItems: 'center', justifyContent: 'center', cursor: 'pointer', 
                        boxShadow: '0 4px 10px rgba(0,0,0,0.2)', zIndex: 1000
                    }}
                >
                    <ChevronDown size={24} />
                </div>
            )}

            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: '6px', backgroundColor: '#f3f4f6', zIndex: 100 }}>
                <div style={{ height: '100%', backgroundColor: '#3b82f6', width: `${progresso}%`, transition: 'width 0.4s ease' }}></div>
            </div>
        </div>
    );
}